import React, { useState, useRef } from 'react';
import { Client } from 'filestack-js/build/main/lib/client';
import errorReporter from '@app/errorReporter';

import { Doka } from '../../vendor/doka';
import Modal, { ModalHeader, ModalTitle } from './Modal';

declare const fileStackClient: Client;

type LockboxPhotoEditorProps = {
  visible: boolean;
  closeModal: () => void;
  listingId: string;
  lockboxPhoto: string;
  onSave: (lockboxPhoto: string) => void;
  isRemovable: boolean;
  $rootScope?: angular.IRootScopeService;
  ToastService?: any;
};

const LockboxPhotoEditor: React.FC<LockboxPhotoEditorProps> = props => {
  const {
    visible,
    closeModal,
    $rootScope,
    ToastService,
    lockboxPhoto,
    onSave,
    isRemovable,
  } = props;
  const imageRef = useRef(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);

  const uploadLockboxPhoto = async () => {
    try {
      const { file } = await imageRef.current.doka.getData();
      if (loading) return;
      setLoading(true);
      const response = await fileStackClient.upload(
        file,
        {
          onProgress: e => {
            setProgress(e.totalPercent);
          },
        },
        { altText: 'lockbox photo' },
      );
      if (!response || !response.url) throw new Error('File uploading failed.');
      setLoading(false);
      setProgress(0);
      onSave(response.url);
    } catch (err) {
      errorReporter.error(err);
      $rootScope?.$evalAsync(() => {
        ToastService?.add({
          type: 'danger',
          message: `Error Processing Lock Box Photo: ${err.message || err}`,
        });
      });
      setLoading(false);
      setProgress(0);
      closeModal();
    }
  };

  return (
    <Modal show={visible} onHide={closeModal}>
      <ModalHeader
        style={{ backgroundColor: '#151515', color: '#cecece', border: 'none' }}
      >
        <ModalTitle className="m-l-md">Edit Lock Box Photo</ModalTitle>
      </ModalHeader>
      <Doka
        src={lockboxPhoto}
        util={'markup'}
        utils={['crop', 'markup', 'resize']}
        height={600}
        cropAllowImageFlipHorizontal={false}
        cropAllowImageFlipVertical={false}
        markupUtil={'draw'}
        markupColor={'#F00'}
        outputCorrectImageExifOrientation={false}
        ref={imageRef}
      />
      {loading ? (
        <div
          style={{
            height: '54px',
            backgroundColor: '#151515',
            display: 'flex',
            alignItems: 'center',
            padding: '20px',
          }}
        >
          <div className="progress-bar-wrapper">
            <div
              className="progress-bar-loader"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      ) : (
        <div className="lockbox-photo-buttons">
          <button
            type="button"
            className="btn btn-success lockbox-photo-button-margin"
            onClick={uploadLockboxPhoto}
            disabled={loading}
          >
            Finish
          </button>
          {isRemovable && (
            <button
              type="button"
              className="btn btn-warning lockbox-photo-button-margin"
              onClick={() => onSave(null)}
              disabled={loading}
            >
              Remove
            </button>
          )}
          <button
            type="button"
            className="btn btn-default"
            onClick={closeModal}
          >
            Close
          </button>
        </div>
      )}
    </Modal>
  );
};

export default LockboxPhotoEditor;
